import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Avatar from '../../app/components/Avatar';
import Icon from '../../app/components/Icon';
import { useApp } from '../../app/lib/AppContext';
import { vnDate } from '../../app/lib/coach';
import { ROLES, SIGN_IN, TIERS, TIER_STYLES, planText } from './labels';
import {
  ActivityGrid, AiUsesBlock, AuditBlock, CoachSectionBlock, CreditsBlock, FeaturesBlock, FeedbackDeviceBlock, ProfileBlock,
  QuickStats, TimelineBlock, dateTime,
} from './blocks';
import HeaderActions from './HeaderActions';
import { CoachFeatureUse, CoachStats, CoachTimeline, SeatsBlock, SetupFunnel, StudentsTable } from './coachBlocks';

// Admin panel › Người dùng › one account: who they are, the plan they're on, how they've used the app over 14 days and
// what they've made (GET /api/admin/users/:id, UserInsight). Counts and labels only; each visit is in the audit log.
// A coach's page has two tabs: "Huấn luyện viên" (their coaching, by default) and "Theo dõi cá nhân" (their own use).
function Header({ h, children }) {
  const label = TIERS.find((t) => t.id === h.plan.tier)?.label;
  const device = h.device && [h.device.os, h.device.app_version && `App ${h.device.app_version}`].filter(Boolean).join(' · ');
  return (
    <section className="flex items-start gap-3 rounded-[20px] bg-base-100 p-4">
      <span className="shrink-0 overflow-hidden rounded-full"><Avatar name={h.name} url={h.avatar_url} size={52} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold">{h.name || 'Không tên'}</h1>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TIER_STYLES[h.plan.tier]}`}>{label}</span>
          {h.student_of && (
            <Link to={`/admin/users/${h.student_of.id}`} className="rounded-full bg-[#e8f0fe] px-2 py-0.5 text-xs font-semibold text-[#1a56db]">
              Học viên · {h.student_of.name}
            </Link>
          )}
          {h.coach && <span className="rounded-full bg-[#15261f] px-2 py-0.5 text-xs font-bold text-[#6fd3a0]">HLV · {h.coach.code}</span>}
        </div>
        <p className="text-sm text-muted">
          #{h.id}
          {h.email && <> · {h.email}</>}
          {h.phone && <> · <a className="text-brand-text underline" href={`tel:${h.phone}`}>{h.phone}</a></>}
        </p>
        <p className="text-sm">{planText(h.plan)}{h.coach && ` · ${h.coach.plan_active ? `${h.coach.seats} chỗ đến ${vnDate(h.coach.plan_ends_at)}` : 'chưa có chỗ HLV'}`}</p>
        <p className="text-xs text-muted">
          {[
            h.sign_in.length ? `Đăng nhập: ${h.sign_in.map((s) => SIGN_IN[s] || s).join(', ')}` : null,
            ROLES[h.role],
            h.coach && h.launch_mode && `Mở app: ${h.launch_mode === 'coach' ? 'chế độ HLV' : h.launch_mode === 'personal' ? 'cá nhân' : 'chế độ dùng gần nhất'}`,
            `Tạo ${vnDate(h.created_at)}`,
            h.last_active_at ? `Hoạt động lần cuối ${dateTime(h.last_active_at)}` : 'Chưa hoạt động',
            device,
          ].filter(Boolean).join(' · ')}
        </p>
        <div className="mt-3 flex flex-col gap-2">{children}</div>
      </div>
    </section>
  );
}

function CoachTab({ coach, audits }) {
  return (
    <>
      <CoachStats stats={coach.stats} />
      <div className="grid gap-3 lg:grid-cols-2">
        <SetupFunnel setup={coach.setup} />
        <SeatsBlock seats={coach.seats} />
      </div>
      <StudentsTable students={coach.students} />
      <div className="grid gap-3 lg:grid-cols-2">
        <CoachFeatureUse use={coach.feature_use} />
        <div className="flex flex-col gap-3">
          <CoachTimeline items={coach.timeline} />
          <AuditBlock audits={audits} />
        </div>
      </div>
    </>
  );
}

export default function UserDetailPage() {
  const { id } = useParams();
  const { api } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('coach');

  const load = useCallback(() => api.get(`/admin/users/${id}`).then(setData)
    .catch((e) => setError(e.status === 404 ? 'Không tìm thấy người dùng' : e.message)), [api, id]);
  useEffect(() => {
    setTab('coach');
    setData(null);
    setError(null);
    load();
  }, [load]);

  // Back to the list as it was (search and filter are in its address); straight to the list when opened directly.
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/admin/users'));

  return (
    <div className="flex flex-col gap-3">
      <button type="button" onClick={back} className="flex min-h-9 w-fit items-center gap-1 text-sm font-semibold text-brand-text">
        <Icon name="chevronLeft" className="size-4" /> Người dùng
      </button>
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-96 rounded-[20px]" />}
      {data && (
        <>
          <Header h={data.header}>
            <HeaderActions data={data} onReload={load}
              onDone={({ header, audits }) => setData((d) => ({ ...d, header: { ...d.header, ...header }, audits }))} />
          </Header>
          {data.coach_tab && (
            <div role="tablist" className="flex gap-1 rounded-[16px] bg-base-300/70 p-1">
              {[['coach', 'Huấn luyện viên'], ['personal', 'Theo dõi cá nhân']].map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  className={`h-11 grow rounded-[12px] px-3 text-sm font-semibold ${tab === key ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>{label}</button>
              ))}
            </div>
          )}
          {data.coach_tab && tab === 'coach' && <CoachTab coach={data.coach_tab} audits={data.audits} />}
          {(!data.coach_tab || tab === 'personal') && (data.coach_tab && !data.personal_used ? (
            <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">Chưa dùng chế độ cá nhân: chưa ghi bữa, buổi tập hay dùng AI cho bản thân.</p>
          ) : (
            <>
              <QuickStats stats={data.stats} />
              <div className="grid gap-3 lg:grid-cols-2">
                <ActivityGrid grid={data.grid} />
                <CreditsBlock credits={data.credits} />
                <AiUsesBlock uses={data.ai_uses} />
                <ProfileBlock profile={data.profile} />
              </div>
              <FeaturesBlock features={data.features} />
              <div className="grid gap-3 lg:grid-cols-2">
                <TimelineBlock key={id} userId={id} initial={data.timeline} />
                <div className="flex flex-col gap-3">
                  {data.coach_section && <CoachSectionBlock section={data.coach_section} />}
                  <FeedbackDeviceBlock feedback={data.feedback} device={data.header.device} />
                  <AuditBlock audits={data.audits} />
                </div>
              </div>
            </>
          ))}
          <p className="text-xs text-muted">Mỗi lần mở trang này được ghi vào nhật ký quản trị. Trang chỉ hiện số liệu tổng, không hiện ảnh món, nội dung nhật ký hay tin nhắn.</p>
        </>
      )}
    </div>
  );
}
